import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { DropdownMenu } from "radix-ui";
import { Link, type To } from "react-router";
import type { LucideIcon } from "lucide-react";
import Text from "../../primitives/components/Text/Text";
import styles from "./toolbar.css";

/**
 * How an action entry hands its work back to the menu that contains it.
 *
 * Opening a dialog from a menu entry has to thread between two Radix
 * behaviours, so the menu — not the entry — owns the choreography:
 *
 *  1. the focus trap is still mounted when `onSelect` fires, and it rejects the
 *     dialog's attempt to focus its first field (focus lands on `<body>`), so
 *     the action runs a tick later, once the menu is gone;
 *  2. Radix then restores focus to the trigger *after* that tick, which would
 *     pull focus straight back out of the now-open dialog, so this one restore
 *     is suppressed.
 *
 * Escape and outside clicks are untouched and still restore focus to the
 * trigger.
 */
const RunActionContext = createContext<((action: () => void) => void) | null>(
  null,
);

/**
 * A toolbar entry that opens a menu instead of navigating — the popover
 * counterpart to `ToolbarButton`.
 *
 * Built on Radix's DropdownMenu rather than a bare Popover: these are menus, so
 * they come with arrow-key navigation, typeahead and `role="menu"` for free.
 * Entries close the menu on select by themselves, so callers only wire up what
 * the entry does.
 *
 * The trigger reuses the `toolbarButton` recipe, so it sits flush with the plain
 * link buttons around it and shares their active state.
 */
export const ToolbarMenu = ({
  id,
  contentId,
  icon: Icon,
  label,
  active = false,
  forceOpen = false,
  children,
}: {
  id?: string;
  /** Put on the flyout itself, so callers can point at the entries inside it. */
  contentId?: string;
  icon: LucideIcon;
  label: string;
  /** Highlights the trigger — e.g. while one of its actions is live. */
  active?: boolean;
  /**
   * Holds the menu open and ignores every dismissal, so something outside can
   * show off its entries — the guided tour points a step at them. Also drops
   * modality, since the user has to keep clicking whatever is doing the showing.
   */
  forceOpen?: boolean;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const skipFocusRestore = useRef(false);
  const runAction = useCallback((action: () => void) => {
    skipFocusRestore.current = true;
    setTimeout(action, 0);
  }, []);

  const isOpen = open || forceOpen;

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setOpen} modal={!forceOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          id={id}
          type="button"
          className={styles.toolbarButton({ active: active || isOpen })}
        >
          <Icon size={24} />
          <Text variant="label" color="neutral-0">
            {label}
          </Text>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        {/* No arrow, per design. */}
        <DropdownMenu.Content
          id={contentId}
          className={styles.menuContent}
          // `sideOffset` measures from the trigger's own edge, but the trigger
          // sits flush inside the toolbar pill's padding+border (8px + 1px) —
          // so this needs to be 12px (target gap from the pill's outer edge)
          // plus that 9px, or the flyout would sit only 3px below the pill.
          sideOffset={21}
          onEscapeKeyDown={(event) => forceOpen && event.preventDefault()}
          onInteractOutside={(event) => forceOpen && event.preventDefault()}
          onCloseAutoFocus={(event) => {
            if (!skipFocusRestore.current) return;
            skipFocusRestore.current = false;
            event.preventDefault();
          }}
        >
          <RunActionContext.Provider value={runAction}>
            {children}
          </RunActionContext.Provider>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

type ToolbarMenuItemBase = {
  icon: LucideIcon;
  label: string;
  /** Accent state — this entry's page is open, or its action is live. */
  active?: boolean;
};

/** An entry either navigates (`to`) or runs an action (`onClick`), never both. */
type ToolbarMenuItemProps =
  | (ToolbarMenuItemBase & { to: To; onClick?: never })
  | (ToolbarMenuItemBase & { onClick: () => void; to?: never });

export const ToolbarMenuItem = (props: ToolbarMenuItemProps) => {
  const { icon: Icon, label, active = false } = props;
  const runAction = useContext(RunActionContext);
  const content = (
    <>
      <Icon size={24} />
      <Text variant="label-small" color="neutral-0">
        {label}
      </Text>
    </>
  );

  // A navigating entry hands its rendering to react-router's Link (`asChild`)
  // so it stays client-side and keeps link semantics (middle-click, open in new
  // tab); an action entry stays a plain menu item.
  if (props.to !== undefined) {
    return (
      <DropdownMenu.Item
        className={styles.menuItem}
        data-active={active}
        asChild
      >
        <Link to={props.to}>{content}</Link>
      </DropdownMenu.Item>
    );
  }

  const { onClick } = props;
  return (
    <DropdownMenu.Item
      className={styles.menuItem}
      data-active={active}
      onSelect={() => (runAction ? runAction(onClick) : onClick())}
    >
      {content}
    </DropdownMenu.Item>
  );
};
